import { confirmDialog, showDialog } from '@/utils/dialogs';
/**
 * MembersScreen (mounted at /students for backward-compat).
 *
 * Unified view of every person enrolled in the school:
 *   • Active students   • Pending student invitations
 *   • Active mentors    • Pending mentor invitations
 *   • Active admins
 *
 * Supports role + status filtering, search, invite via role picker,
 * and revoke / resend for any pending invitation.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  RefreshControl,
  Modal,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { apiRequest } from '../../services/apiClient';
import {
  studentAPI,
  mentorAPI,
  schoolAdminAPI,
  StudentInvitation,
  SchoolAdminInfo,
  AdminInvitation,
} from '../../services/api';
import { MentorInfo, MentorInvitation } from '../../types/sharing';
import InvitePersonModal, { type InviteRole } from '../../components/school/InvitePersonModal';
import StudentDetailModal from '../../components/school/StudentDetailModal';
import { colors } from '@/design-system';

// ── Types ────────────────────────────────────────────────────────────────

interface ApiStudent {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  created_at: string;
  last_activity?: string | null;
  transcripts_total?: number;
  chat_messages_total?: number;
  avg_icf_score?: number | null;
}

type Role = 'student' | 'mentor' | 'admin';
type StatusKind = 'active' | 'inactive' | 'invited';

type Row =
  | { kind: 'user'; role: Role; status: StatusKind; id: number; name: string; email: string;
      subtitle?: string; extra?: Array<{ icon: keyof typeof MaterialIcons.glyphMap; label: string }>; }
  | { kind: 'invite'; role: Role; invitationId: number; name: string; email: string;
      expiresAt: string; };

type RoleFilter = 'all' | Role;
type StatusFilter = 'all' | StatusKind;

// ── Design tokens ────────────────────────────────────────────────────────

const COLORS = {
  background: '#F9FAFB',
  surface: '#FFFFFF',
  primary: colors.primary,
  border: '#E5E7EB',
  text: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#6B7280',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

const ROLE_BADGE: Record<Role, { bg: string; fg: string; icon: keyof typeof MaterialIcons.glyphMap }> = {
  student: { bg: '#DBEAFE', fg: '#1D4ED8', icon: 'school' },
  mentor:  { bg: '#FEF3C7', fg: '#B45309', icon: 'psychology' },
  admin:   { bg: '#EDE9FE', fg: '#6D28D9', icon: 'shield' },
};

const STATUS_STYLE: Record<StatusKind, { bg: string; fg: string; labelKey: string; fallback: string }> = {
  active:   { bg: '#DCFCE7', fg: '#15803D', labelKey: 'members.statusActive',   fallback: 'Active' },
  inactive: { bg: '#F3F4F6', fg: '#4B5563', labelKey: 'members.statusInactive', fallback: 'Inactive' },
  invited:  { bg: '#DBEAFE', fg: '#1D4ED8', labelKey: 'members.statusInvited',  fallback: 'Invited' },
};

// ─────────────────────────────────────────────────────────────────────────

export default function StudentListScreen() {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { user } = useAuth();

  const schoolId = user?.school_id ?? null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [students, setStudents] = useState<ApiStudent[]>([]);
  const [studentInvites, setStudentInvites] = useState<StudentInvitation[]>([]);
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [mentorInvites, setMentorInvites] = useState<MentorInvitation[]>([]);
  const [admins, setAdmins] = useState<SchoolAdminInfo[]>([]);
  const [adminInvites, setAdminInvites] = useState<AdminInvitation[]>([]);

  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const [showInvitePicker, setShowInvitePicker] = useState(false);
  const [invitePerson, setInvitePerson] = useState<{ visible: boolean; role: InviteRole }>({
    visible: false,
    role: 'student',
  });
  const closeInvite = () => setInvitePerson((prev) => ({ ...prev, visible: false }));
  const [showAdminInvite, setShowAdminInvite] = useState(false);
  const [adminInviteEmail, setAdminInviteEmail] = useState('');
  const [adminInviteName, setAdminInviteName] = useState('');
  const [adminInviteSubmitting, setAdminInviteSubmitting] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // ── Data loading ───────────────────────────────────────────────────────

  const load = useCallback(
    async (isRefresh = false) => {
      if (!schoolId) {
        setLoading(false);
        return;
      }
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const [stu, stuInv, men, menInv, adm, admInv] = await Promise.all([
          apiRequest<ApiStudent[]>({ endpoint: `/schools/${schoolId}/students`, method: 'GET' }).catch(
            () => [] as ApiStudent[],
          ),
          studentAPI.listStudentInvitations(schoolId, 'pending').catch(() => []),
          mentorAPI.listMentors(schoolId).catch(() => [] as MentorInfo[]),
          mentorAPI.listMentorInvitations(schoolId).catch(() => [] as MentorInvitation[]),
          schoolAdminAPI.listAdmins(schoolId).catch(() => [] as SchoolAdminInfo[]),
          schoolAdminAPI.listAdminInvitations(schoolId, 'pending').catch(() => [] as AdminInvitation[]),
        ]);
        setStudents(stu || []);
        setStudentInvites(stuInv || []);
        setMentors(men || []);
        setMentorInvites(menInv || []);
        setAdmins(adm || []);
        setAdminInvites(admInv || []);
      } catch (e: any) {
        setError(e?.message || t('members.loadFailed'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [schoolId, t],
  );

  useEffect(() => {
    load();
  }, [load]);

  // ── Row assembly ───────────────────────────────────────────────────────

  const allRows: Row[] = useMemo(() => {
    const rows: Row[] = [];

    for (const s of students) {
      const extra: Array<{ icon: keyof typeof MaterialIcons.glyphMap; label: string }> = [];
      if (s.transcripts_total != null) extra.push({ icon: 'description', label: `${s.transcripts_total}` });
      if (s.chat_messages_total != null) extra.push({ icon: 'chat', label: `${s.chat_messages_total}` });
      if (s.avg_icf_score != null) extra.push({ icon: 'star', label: s.avg_icf_score.toFixed(1) });
      rows.push({
        kind: 'user',
        role: 'student',
        status: s.is_active ? 'active' : 'inactive',
        id: s.id,
        name: s.full_name || s.email,
        email: s.email,
        subtitle: s.last_activity
          ? t('members.lastActivity', { date: new Date(s.last_activity).toLocaleDateString(), defaultValue: `Last active ${new Date(s.last_activity).toLocaleDateString()}` })
          : undefined,
        extra,
      });
    }

    for (const m of mentors) {
      rows.push({
        kind: 'user',
        role: 'mentor',
        status: 'active',
        id: m.id,
        name: m.full_name || m.email,
        email: m.email,
        subtitle: t('members.mentorSince', { date: new Date(m.created_at).toLocaleDateString(), defaultValue: `Mentor since ${new Date(m.created_at).toLocaleDateString()}` }),
      });
    }

    for (const a of admins) {
      rows.push({
        kind: 'user',
        role: 'admin',
        status: a.is_active ? 'active' : 'inactive',
        id: a.id,
        name: a.full_name || a.email,
        email: a.email,
      });
    }

    for (const inv of studentInvites) {
      rows.push({
        kind: 'invite',
        role: 'student',
        invitationId: inv.id,
        name: inv.full_name || inv.email,
        email: inv.email,
        expiresAt: inv.invitation_expires,
      });
    }
    for (const inv of mentorInvites) {
      rows.push({
        kind: 'invite',
        role: 'mentor',
        invitationId: inv.id,
        name: inv.full_name || inv.email,
        email: inv.email,
        expiresAt: (inv as any).invitation_expires || inv.expires_at,
      });
    }
    for (const inv of adminInvites) {
      rows.push({
        kind: 'invite',
        role: 'admin',
        invitationId: inv.id,
        name: inv.full_name || inv.email,
        email: inv.email,
        expiresAt: inv.invitation_expires || new Date().toISOString(),
      });
    }
    return rows;
  }, [students, mentors, admins, studentInvites, mentorInvites, adminInvites, t]);

  const counts = useMemo(() => {
    const byRole = { student: 0, mentor: 0, admin: 0 } as Record<Role, number>;
    let active = 0, invited = 0, inactive = 0;
    for (const r of allRows) {
      byRole[r.role] += 1;
      if (r.kind === 'invite') invited += 1;
      else if (r.status === 'active') active += 1;
      else inactive += 1;
    }
    return { total: allRows.length, active, invited, inactive, byRole };
  }, [allRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (roleFilter !== 'all' && r.role !== roleFilter) return false;
      if (statusFilter !== 'all') {
        const status = r.kind === 'invite' ? 'invited' : r.status;
        if (status !== statusFilter) return false;
      }
      if (q) {
        if (!r.name.toLowerCase().includes(q) && !r.email.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allRows, roleFilter, statusFilter, search]);

  // ── Actions ────────────────────────────────────────────────────────────

  const confirm = async (title: string, message: string, onConfirm: () => void) => {
    const confirmed = await confirmDialog(
      title,
      message,
      t('common.confirm'),
      t('common.cancel'),
    );
    if (confirmed) onConfirm();
  };

  const handleRevoke = (role: Role, invitationId: number) => {
    if (!schoolId) return;
    const key = `${role}-${invitationId}`;
    confirm(
      t('members.revokeTitle'),
      t('members.revokeMessage'),
      async () => {
        try {
          setRevokingId(key);
          if (role === 'student') {
            await studentAPI.cancelStudentInvitation(schoolId, invitationId);
          } else if (role === 'mentor') {
            await mentorAPI.cancelMentorInvitation(schoolId, invitationId);
          } else if (role === 'admin') {
            await schoolAdminAPI.cancelAdminInvitation(schoolId, invitationId);
          }
          await load(true);
        } catch (e: any) {
          showDialog(
            t('common.error'),
            e?.response?.data?.detail || e?.message || t('members.revokeFailed'),
          );
        } finally {
          setRevokingId(null);
        }
      },
    );
  };

  const handleResend = async (role: Role, invitationId: number) => {
    if (!schoolId) return;
    const key = `${role}-${invitationId}`;
    try {
      setResendingId(key);
      if (role === 'student') {
        await studentAPI.resendStudentInvitation(schoolId, invitationId);
      } else if (role === 'mentor') {
        await mentorAPI.resendMentorInvitation(schoolId, invitationId);
      } else if (role === 'admin') {
        await schoolAdminAPI.resendAdminInvitation(schoolId, invitationId);
      }
      showDialog(
        t('members.resendSuccessTitle'),
        t('members.resendSuccessMessage'),
      );
      await load(true);
    } catch (e: any) {
      showDialog(
        t('common.error'),
        e?.response?.data?.detail || e?.message || t('members.resendFailed'),
      );
    } finally {
      setResendingId(null);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────

  const renderChip = <T extends string>(
    value: T,
    active: T,
    setActive: React.Dispatch<React.SetStateAction<T>>,
    label: string,
  ) => (
    <TouchableOpacity
      key={value}
      onPress={() => setActive(value)}
      className={`px-3 py-1.5 rounded-full mr-2 mb-2 ${active === value ? 'bg-primary' : 'bg-gray-200'}`}
    >
      <Text className={`text-xs font-medium ${active === value ? 'text-white' : 'text-gray-700'}`}>
        {label}
      </Text>
    </TouchableOpacity>
  );

  const renderRoleBadge = (role: Role) => {
    const s = ROLE_BADGE[role];
    const label = t(`members.role.${role}`);
    return (
      <View style={{ backgroundColor: s.bg }} className="flex-row items-center px-2 py-0.5 rounded-full">
        <MaterialIcons name={s.icon} size={12} color={s.fg} />
        <Text style={{ color: s.fg }} className="text-[11px] font-semibold ml-1">{label}</Text>
      </View>
    );
  };

  const renderStatusBadge = (status: StatusKind) => {
    const s = STATUS_STYLE[status];
    return (
      <View style={{ backgroundColor: s.bg }} className="px-2 py-0.5 rounded-full">
        <Text style={{ color: s.fg }} className="text-[11px] font-semibold">
          {t(s.labelKey, s.fallback)}
        </Text>
      </View>
    );
  };

  const renderRow = (row: Row) => {
    const isInvite = row.kind === 'invite';
    const key = isInvite ? `inv-${row.role}-${row.invitationId}` : `user-${row.role}-${row.id}`;

    const body = (
      <Card>
        <CardContent className="p-4">
          <View className="flex-row items-start justify-between mb-2">
            <View className="flex-1 pr-2">
              <Text className="text-base font-semibold text-foreground" numberOfLines={1}>
                {row.name}
              </Text>
              <Text className="text-sm text-muted-foreground" numberOfLines={1}>
                {row.email}
              </Text>
            </View>
            <View className="items-end gap-1">
              {renderRoleBadge(row.role)}
              {renderStatusBadge(isInvite ? 'invited' : row.status)}
            </View>
          </View>

          {isInvite ? (
            <View>
              <View className="flex-row items-center mb-3">
                <MaterialIcons name="schedule" size={13} color={COLORS.textMuted} />
                <Text className="text-xs text-muted-foreground ml-1">
                  {t('members.inviteExpires', {
                    date: new Date(row.expiresAt).toLocaleDateString(),
                    defaultValue: `Expires ${new Date(row.expiresAt).toLocaleDateString()}`,
                  })}
                </Text>
              </View>
              <View className="flex-row justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => handleResend(row.role, row.invitationId)}
                  disabled={resendingId === `${row.role}-${row.invitationId}`}
                >
                  {resendingId === `${row.role}-${row.invitationId}` ? (
                    <ActivityIndicator size="small" color={COLORS.text} />
                  ) : (
                    <>
                      <MaterialIcons name="send" size={14} color={COLORS.text} />
                      <Text className="text-xs ml-1">{t('members.resend')}</Text>
                    </>
                  )}
                </Button>
                <TouchableOpacity
                  className="px-3 py-1.5 rounded-md border border-red-200 bg-red-50 flex-row items-center"
                  onPress={() => handleRevoke(row.role, row.invitationId)}
                  disabled={revokingId === `${row.role}-${row.invitationId}`}
                >
                  {revokingId === `${row.role}-${row.invitationId}` ? (
                    <ActivityIndicator size="small" color={COLORS.error} />
                  ) : (
                    <>
                      <MaterialIcons name="close" size={14} color={COLORS.error} />
                      <Text className="text-xs text-red-700 ml-1">
                        {t('members.revoke')}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <>
              {row.subtitle ? (
                <Text className="text-xs text-muted-foreground mb-1">{row.subtitle}</Text>
              ) : null}
              {row.extra && row.extra.length > 0 ? (
                <View className="flex-row flex-wrap gap-x-4 gap-y-1 mt-1">
                  {row.extra.map((e, i) => (
                    <View key={i} className="flex-row items-center">
                      <MaterialIcons name={e.icon} size={13} color={COLORS.textMuted} />
                      <Text className="text-xs text-muted-foreground ml-1">{e.label}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    );

    // Students open detail modal; others are non-interactive rows
    if (!isInvite && row.role === 'student') {
      return (
        <TouchableOpacity
          key={key}
          onPress={() => setSelectedStudentId(row.id)}
          activeOpacity={0.7}
        >
          {body}
        </TouchableOpacity>
      );
    }
    return <View key={key}>{body}</View>;
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (!schoolId) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.background }}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <MaterialIcons name="lock-outline" size={48} color={COLORS.textMuted} />
          <Text className="text-base text-foreground text-center mt-4">
            {t('members.noSchoolAccess')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.background }} edges={['top']}>
      <ScrollView
        contentContainerStyle={{ paddingBottom: insets.bottom + 80 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => load(true)} tintColor={COLORS.primary} />
        }
      >
        <View className="px-5 pt-4">
          {/* Header */}
          <View className="flex-row items-start justify-between mb-4">
            <View className="flex-1">
              <Text className="text-2xl font-bold text-foreground">
                {t('members.title')}
              </Text>
              <Text className="text-sm text-muted-foreground mt-0.5">
                {t('members.summaryLine', {
                  total: counts.total,
                  active: counts.active,
                  invited: counts.invited,
                  defaultValue: `${counts.total} total • ${counts.active} active • ${counts.invited} invited`,
                })}
              </Text>
            </View>
            <Button
              variant="default"
              size="sm"
              onPress={() => setShowInvitePicker(true)}
            >
              <MaterialIcons name="person-add" size={16} color="white" />
              <Text className="text-white text-xs ml-1">
                {t('members.invite')}
              </Text>
            </Button>
          </View>

          {/* Search */}
          {allRows.length > 0 && (
            <View className="mb-3 bg-white rounded-lg border border-gray-200 flex-row items-center px-3">
              <MaterialIcons name="search" size={18} color={COLORS.textMuted} />
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder={t('members.searchPlaceholder')}
                placeholderTextColor={COLORS.textMuted}
                className="flex-1 py-2 px-2 text-foreground"
                autoCapitalize="none"
                autoCorrect={false}
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <MaterialIcons name="close" size={18} color={COLORS.textMuted} />
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Role filter chips */}
          {allRows.length > 0 && (
            <View className="flex-row flex-wrap">
              {renderChip('all', roleFilter, setRoleFilter,
                t('members.roleFilterAll', { count: counts.total, defaultValue: `Everyone (${counts.total})` }))}
              {renderChip('student', roleFilter, setRoleFilter,
                t('members.roleFilterStudents', { count: counts.byRole.student, defaultValue: `Students (${counts.byRole.student})` }))}
              {renderChip('mentor', roleFilter, setRoleFilter,
                t('members.roleFilterMentors', { count: counts.byRole.mentor, defaultValue: `Mentors (${counts.byRole.mentor})` }))}
              {renderChip('admin', roleFilter, setRoleFilter,
                t('members.roleFilterAdmins', { count: counts.byRole.admin, defaultValue: `Admins (${counts.byRole.admin})` }))}
            </View>
          )}

          {/* Status filter chips */}
          {allRows.length > 0 && (
            <View className="flex-row flex-wrap mb-4">
              {renderChip('all', statusFilter, setStatusFilter, t('members.statusFilterAll'))}
              {renderChip('active', statusFilter, setStatusFilter,
                t('members.statusFilterActive', { count: counts.active, defaultValue: `Active (${counts.active})` }))}
              {counts.invited > 0 &&
                renderChip('invited', statusFilter, setStatusFilter,
                  t('members.statusFilterInvited', { count: counts.invited, defaultValue: `Invited (${counts.invited})` }))}
              {counts.inactive > 0 &&
                renderChip('inactive', statusFilter, setStatusFilter,
                  t('members.statusFilterInactive', { count: counts.inactive, defaultValue: `Inactive (${counts.inactive})` }))}
            </View>
          )}

          {/* Body */}
          {loading ? (
            <View className="items-center py-16">
              <ActivityIndicator size="large" color={COLORS.primary} />
              <Text className="text-sm text-muted-foreground mt-3">
                {t('members.loading')}
              </Text>
            </View>
          ) : error ? (
            <Card>
              <CardContent className="p-6 items-center">
                <MaterialIcons name="error-outline" size={40} color={COLORS.error} />
                <Text className="text-base text-foreground mt-3 mb-4 text-center">{error}</Text>
                <Button variant="outline" size="sm" onPress={() => load(true)}>
                  <Text>{t('common.retry')}</Text>
                </Button>
              </CardContent>
            </Card>
          ) : counts.total === 0 ? (
            <Card>
              <CardContent className="p-8 items-center">
                <MaterialIcons name="groups" size={48} color={COLORS.textMuted} />
                <Text className="text-lg font-semibold text-foreground mt-3 mb-1">
                  {t('members.emptyTitle')}
                </Text>
                <Text className="text-sm text-muted-foreground text-center mb-4">
                  {t('members.emptyDesc')}
                </Text>
                <Button variant="default" onPress={() => setShowInvitePicker(true)}>
                  <MaterialIcons name="person-add" size={16} color="white" />
                  <Text className="text-white ml-2">
                    {t('members.inviteFirst')}
                  </Text>
                </Button>
              </CardContent>
            </Card>
          ) : filteredRows.length === 0 ? (
            <Card>
              <CardContent className="p-8 items-center">
                <MaterialIcons name="filter-list" size={40} color={COLORS.textMuted} />
                <Text className="text-base text-foreground mt-3 text-center">
                  {t('members.emptyFiltered')}
                </Text>
              </CardContent>
            </Card>
          ) : (
            <View className="gap-3">{filteredRows.map(renderRow)}</View>
          )}
        </View>
      </ScrollView>

      {/* Role picker — shows when tapping Invite */}
      <Modal
        visible={showInvitePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowInvitePicker(false)}
      >
        <View className="flex-1 bg-black/50 justify-center items-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <Text className="text-lg font-bold text-foreground">
                {t('members.invitePickerTitle')}
              </Text>
              <Text className="text-sm text-muted-foreground mt-1">
                {t('members.invitePickerSubtitle')}
              </Text>
            </CardHeader>
            <CardContent>
              <View className="gap-2">
                <TouchableOpacity
                  className="flex-row items-center p-3 border border-gray-200 rounded-lg"
                  onPress={() => {
                    setShowInvitePicker(false);
                    setInvitePerson({ visible: true, role: 'student' });
                  }}
                >
                  <View style={{ backgroundColor: ROLE_BADGE.student.bg }} className="w-9 h-9 rounded-full items-center justify-center">
                    <MaterialIcons name={ROLE_BADGE.student.icon} size={18} color={ROLE_BADGE.student.fg} />
                  </View>
                  <View className="flex-1 ml-3">
                    <Text className="text-sm font-semibold text-foreground">
                      {t('members.role.student')}
                    </Text>
                    <Text className="text-xs text-muted-foreground">
                      {t('members.inviteStudentDesc')}
                    </Text>
                  </View>
                  <MaterialIcons name="chevron-right" size={20} color={COLORS.textMuted} />
                </TouchableOpacity>

                <TouchableOpacity
                  className="flex-row items-center p-3 border border-gray-200 rounded-lg"
                  onPress={() => {
                    setShowInvitePicker(false);
                    setInvitePerson({ visible: true, role: 'mentor' });
                  }}
                >
                  <View style={{ backgroundColor: ROLE_BADGE.mentor.bg }} className="w-9 h-9 rounded-full items-center justify-center">
                    <MaterialIcons name={ROLE_BADGE.mentor.icon} size={18} color={ROLE_BADGE.mentor.fg} />
                  </View>
                  <View className="flex-1 ml-3">
                    <Text className="text-sm font-semibold text-foreground">
                      {t('members.role.mentor')}
                    </Text>
                    <Text className="text-xs text-muted-foreground">
                      {t('members.inviteMentorDesc')}
                    </Text>
                  </View>
                  <MaterialIcons name="chevron-right" size={20} color={COLORS.textMuted} />
                </TouchableOpacity>

                <TouchableOpacity
                  className="flex-row items-center p-3 border border-gray-200 rounded-lg"
                  onPress={() => {
                    setShowInvitePicker(false);
                    setAdminInviteEmail('');
                    setAdminInviteName('');
                    setShowAdminInvite(true);
                  }}
                >
                  <View style={{ backgroundColor: ROLE_BADGE.admin.bg }} className="w-9 h-9 rounded-full items-center justify-center">
                    <MaterialIcons name={ROLE_BADGE.admin.icon} size={18} color={ROLE_BADGE.admin.fg} />
                  </View>
                  <View className="flex-1 ml-3">
                    <Text className="text-sm font-semibold text-foreground">
                      {t('members.role.admin')}
                    </Text>
                    <Text className="text-xs text-muted-foreground">
                      {t('members.inviteAdminDesc')}
                    </Text>
                  </View>
                  <MaterialIcons name="chevron-right" size={20} color={COLORS.textMuted} />
                </TouchableOpacity>
              </View>
              <View className="flex-row justify-end mt-3">
                <Button variant="outline" size="sm" onPress={() => setShowInvitePicker(false)}>
                  <Text>{t('common.cancel')}</Text>
                </Button>
              </View>
            </CardContent>
          </Card>
        </View>
      </Modal>

      {/* Invite modals + detail */}
      {schoolId && (
        <InvitePersonModal
          visible={invitePerson.visible}
          schoolId={schoolId}
          initialRole={invitePerson.role}
          allowedRoles={['student', 'mentor']}
          onClose={closeInvite}
          onInvited={() => load(true)}
        />
      )}

      {/* Admin invite modal (built inline — lightweight) */}
      <Modal
        visible={showAdminInvite}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAdminInvite(false)}
      >
        <View className="flex-1 bg-black/50 justify-center items-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <Text className="text-lg font-bold text-foreground">
                {t('members.inviteAdminTitle')}
              </Text>
              <Text className="text-sm text-muted-foreground mt-1">
                {t('members.inviteAdminSubtitle')}
              </Text>
            </CardHeader>
            <CardContent>
              <Text className="text-sm font-medium text-foreground mb-1">
                {t('members.fullNameLabel')}
              </Text>
              <TextInput
                value={adminInviteName}
                onChangeText={setAdminInviteName}
                placeholder={t('members.fullNamePlaceholder')}
                className="border border-gray-300 rounded-lg px-3 py-2 mb-3 text-foreground"
                editable={!adminInviteSubmitting}
                maxLength={120}
              />
              <Text className="text-sm font-medium text-foreground mb-1">
                {t('members.emailLabel')}
              </Text>
              <TextInput
                value={adminInviteEmail}
                onChangeText={setAdminInviteEmail}
                placeholder="name@example.com"
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                className="border border-gray-300 rounded-lg px-3 py-2 text-foreground"
                editable={!adminInviteSubmitting}
              />
              <View className="flex-row justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setShowAdminInvite(false)}
                  disabled={adminInviteSubmitting}
                >
                  <Text>{t('common.cancel')}</Text>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  disabled={
                    adminInviteSubmitting ||
                    !adminInviteEmail.trim() ||
                    !adminInviteName.trim()
                  }
                  onPress={async () => {
                    if (!schoolId) return;
                    try {
                      setAdminInviteSubmitting(true);
                      await schoolAdminAPI.inviteAdmin(
                        schoolId,
                        adminInviteEmail.trim(),
                        adminInviteName.trim(),
                      );
                      setShowAdminInvite(false);
                      setAdminInviteEmail('');
                      setAdminInviteName('');
                      await load(true);
                    } catch (e: any) {
                      showDialog(
                        t('common.error'),
                        e?.response?.data?.detail ||
                          e?.message ||
                          t('members.inviteAdminFailed'),
                      );
                    } finally {
                      setAdminInviteSubmitting(false);
                    }
                  }}
                >
                  {adminInviteSubmitting ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text className="text-white">{t('members.sendInvite')}</Text>
                  )}
                </Button>
              </View>
            </CardContent>
          </Card>
        </View>
      </Modal>

      {schoolId && (
        <StudentDetailModal
          visible={selectedStudentId !== null}
          studentId={selectedStudentId}
          schoolId={schoolId}
          onClose={() => setSelectedStudentId(null)}
        />
      )}
    </SafeAreaView>
  );
}
